/* eslint-disable prettier/prettier */
import React, { useRef, useLayoutEffect, useState, useMemo, useEffect } from 'react';
/*
 * @Author: princemwang
 * @Date: 2023-02-09 10:45:42
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2023-02-23 18:06:20
 */
const AudioMarkItem = ({
  style,
  text,
  timeText,
  processWidth,
}: {
  style: React.CSSProperties;
  text: string;
  timeText: string;
  processWidth: number;
}) => {
  const maskRef = useRef(null);
  const markRef = useRef<HTMLDivElement>(null);
  const [maskLeft, setMaskLeft] = useState(0);
  const [height, setHeight] = useState(0);
  // tooltip 显隐：点击/轻触打点圆点 -> 显示；鼠标移出圆点 -> 隐藏。
  const [visible, setVisible] = useState(false);
  // 显示：mousedown/touchstart 不阻止冒泡，让事件继续到父级 customize-audio 触发 seek(进度条跳转)。
  useEffect(() => {
    const el = markRef.current;
    if (!el) return;
    const show = () => setVisible(true);
    el.addEventListener('mousedown', show, false);
    el.addEventListener('touchstart', show, false);
    return () => {
      el.removeEventListener('mousedown', show, false);
      el.removeEventListener('touchstart', show, false);
    };
  }, []);
  // 隐藏：不能用圆点的 mouseleave —— seek 后层级更高的进度圆点(progess-bar, z-index:9001)会移动并盖到灰点上，
  // 导致命中目标变成进度圆点、灰点瞬间触发 mouseleave。改用「几何坐标」判断：
  // tooltip 显示期间监听 mousemove，只有指针真正移出灰点矩形范围(含少量容差)才隐藏，不受上层遮挡影响。
  useEffect(() => {
    if (!visible) return;
    // 判断坐标是否落在当前灰点矩形内(含 4px 容差)
    const inMark = (clientX: number, clientY: number) => {
      const el = markRef.current;
      if (!el) return false;
      const r = el.getBoundingClientRect();
      const pad = 4;
      return (
        clientX >= r.left - pad &&
        clientX <= r.right + pad &&
        clientY >= r.top - pad &&
        clientY <= r.bottom + pad
      );
    };
    // PC：鼠标移出灰点矩形即隐藏
    const onMove = (e: MouseEvent) => {
      if (!inMark(e.clientX, e.clientY)) setVisible(false);
    };
    // 移动端：无 mousemove。触点不在当前灰点内即隐藏(音频轨道/其它灰点)。
    // 点其它灰点时：该灰点的 show 显示新 tooltip，本灰点这里的监听隐藏旧 tooltip，实现切换。
    const onTouch = (e: TouchEvent) => {
      const t = e.touches[0] || e.changedTouches[0];
      if (!t) return;
      if (!inMark(t.clientX, t.clientY)) setVisible(false);
    };
    document.addEventListener('mousemove', onMove, false);
    // 必须用「捕获阶段」：seek 的 downHandle 绑在父级 customize-audio 的冒泡阶段且会 stopPropagation，
    // 冒泡阶段的 document 监听收不到 touchstart；捕获阶段从 document 往下传，早于 downHandle 执行，一定能收到。
    // 延迟绑定，避免本次打开灰点的 touchstart 立刻把它关掉。
    const timer = window.setTimeout(() => {
      document.addEventListener('touchstart', onTouch, true);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousemove', onMove, false);
      document.removeEventListener('touchstart', onTouch, true);
    };
  }, [visible]);
  useLayoutEffect(() => {
    if (!maskRef.current) return;
    const { width, height } = maskRef.current.getBoundingClientRect();
    setHeight(height);
    if (width) {
      setMaskLeft(Math.floor(width / 2));
    }
  }, [maskRef]);
  const styLeft = useMemo(() => {
    const left =
      Number(style.left) - maskLeft < 0
        ? 0
        : Math.floor(Number(style.left)) >= Math.floor(processWidth) - 328
          ? Math.floor(processWidth - 328)
          : Number(style.left) - maskLeft;
    return left;
  }, [style, maskLeft, processWidth]);
  return (
    <>
      <div className={'progress-mark mark-init'} style={{ left: `${style.left}px` }} ref={markRef}></div>
      <div
        className={`audio-trapezoidal-mask audio-init-mask${visible ? ' mask-visible' : ''}`}
        style={{ left: `${styLeft}px`, top: `-${height + 20}px` }}
        ref={maskRef}
      >
        <div className="audio-trapezoidal-maskText">{timeText}</div>
        <div className="audio-trapezoidal-maskText">{text}</div>
      </div>
    </>
  );
};
export default AudioMarkItem;
